import React, { useEffect, useRef, useState } from 'react'
import { MbscCalendarEvent, MbscSelectCloseEvent, Select } from '@mobiscroll/react-latest'
import moment from 'moment'
import BusyTimesBarGraph from './BusyTimesBarGraph'
import { MoonLoader } from 'react-spinners'

const weekDays = [
  { text: 'Sunday', value: '0' },
  { text: 'Monday', value: '1' },
  { text: 'Tuesday', value: '2' },
  { text: 'Wednesday', value: '3' },
  { text: 'Thursday', value: '4' },
  { text: 'Friday', value: '5' },
  { text: 'Saturday', value: '6' }
]

interface ITimelineViewBusyTimesProps {
  currentDate: Date
  setCurrentDate: (value: Date) => void
  trips: MbscCalendarEvent[]
}

const TimelineViewBusyTimes = (props: ITimelineViewBusyTimesProps) => {
  const { currentDate, setCurrentDate, trips } = props
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [debouncedTrips, setDebouncedTrips] = useState(props.trips)
  const [tripCountPerHour, setTripCountPerHour] = useState<number[]>(Array(24).fill(0))
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [selectedDay, setSelectedDay] = useState<string>(
    moment()
      .day()
      .toString()
  )

  useEffect(() => {
    setSelectedDay(
      moment(currentDate)
        .day()
        .toString()
    )
  }, [currentDate])

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      setDebouncedTrips(props.trips)
    }, 500)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [trips])

  useEffect(() => {
    if (debouncedTrips.length !== 0) {
      const filteredTrips = debouncedTrips.filter(trip => {
        const tripStart = moment(trip.start as string)
        const tripEnd = moment(trip.end as string)
        const currentDay = moment(currentDate)

        return tripStart.isSame(currentDay, 'day') || tripEnd.isSame(currentDay, 'day')
      })

      countTripsPerHour(filteredTrips)
    } else {
      countTripsPerHour([])
    }
  }, [debouncedTrips, currentDate])

  const countTripsPerHour = (trips: MbscCalendarEvent[]) => {
    setIsLoading(true)
    const tripCountPerHour = Array(24).fill(0)

    trips.forEach(trip => {
      const pickupTime = new Date((trip.start as string) || '')
      const dropoffTime = new Date((trip.end as string) || '')

      const pickupLocalTime = moment(pickupTime)
      const dropoffLocalTime = moment(dropoffTime)
      const currentDay = moment(currentDate)

      const startHour = pickupLocalTime.isBefore(currentDay, 'day') ? 0 : pickupLocalTime.hours()
      const endHour = dropoffLocalTime.isAfter(currentDay, 'day') ? 23 : dropoffLocalTime.hours()

      for (let hour = startHour; hour <= endHour; hour++) {
        tripCountPerHour[hour]++
      }
    })

    setTripCountPerHour(tripCountPerHour)
    setIsLoading(false)
    return tripCountPerHour
  }

  const handleChange = (data: MbscSelectCloseEvent) => {
    setSelectedDay(data.value)
    const newDate = moment(currentDate)
      .weekday(data.value)
      .toDate()
    setCurrentDate(newDate)
  }

  return (
    <div className='busy-times-section'>
      <div className='busy-times-header'>
        <div>Busy Times</div>
        <div className='busy-times-day-selector'>
          <Select data={weekDays} value={selectedDay} onClose={handleChange} theme='windows' themeVariant='light' />
        </div>
      </div>
      <div className='busy-times-graph-container'>
        {isLoading ? (
          <MoonLoader sizeUnit={'px'} size={20} color={'#405264'} loading={true} />
        ) : tripCountPerHour.every(count => count === 0) ? (
          <div className='busy-times-empty-state'>No trips scheduled for this day.</div>
        ) : (
          <BusyTimesBarGraph data={tripCountPerHour} />
        )}
      </div>
    </div>
  )
}

export default TimelineViewBusyTimes



// WEBPACK FOOTER //
// ./src/components/trips/timeline-view/TimeLineViewBusyTimes.tsx